import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { tenantApi } from '../../services/api';
import TenantHeader from '../../components/msp/TenantHeader';
import './Msp.css';

const normalizeList = (payload, key) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.[key])) return payload[key];
  return [];
};

function TenantAssets() {
  const { tenantId } = useParams();
  const [assets, setAssets] = useState([]);
  const [status, setStatus] = useState('loading');
  const [filterTag, setFilterTag] = useState('');
  const [filterEnvironment, setFilterEnvironment] = useState('');
  const [selectedAssetId, setSelectedAssetId] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [hostingProvider, setHostingProvider] = useState('');
  const [assetTags, setAssetTags] = useState('');
  const [metadataStatus, setMetadataStatus] = useState('idle');
  const [monitorStatus, setMonitorStatus] = useState('idle');

  useEffect(() => {
    let isMounted = true;
    setStatus('loading');

    tenantApi
      .listAssets(tenantId)
      .then((response) => {
        if (!isMounted) return;
        setAssets(normalizeList(response.data, 'assets'));
        setStatus('ready');
      })
      .catch(() => {
        if (isMounted) {
          setStatus('error');
        }
      });

    return () => {
      isMounted = false;
    };
  }, [tenantId]);

  const primaryDomain = assets.find((asset) => asset.is_primary)?.domain || assets[0]?.domain || '—';
  const verified = assets.find((asset) => asset.is_primary)?.verified || assets[0]?.verified;
  const subdomains = assets.filter((asset) => asset.type === 'subdomain' || asset.subdomain);
  const services = assets.filter((asset) => asset.port || asset.service);
  const tlsAssets = assets.filter((asset) => asset.tls || asset.certificate);
  const taggedAssets = assets.filter((asset) => asset.tags?.length || asset.owner || asset.hosting_provider);
  const filteredAssets = assets.filter((asset) => {
    const tagMatch = filterTag
      ? Array.isArray(asset.tags)
        ? asset.tags.join(',').toLowerCase().includes(filterTag.toLowerCase())
        : String(asset.tags || '').toLowerCase().includes(filterTag.toLowerCase())
      : true;
    const envMatch = filterEnvironment
      ? (asset.environment || asset.env || '').toLowerCase() === filterEnvironment.toLowerCase()
      : true;
    return tagMatch && envMatch;
  });

  const handleMetadataSave = async (event) => {
    event.preventDefault();
    if (!selectedAssetId) return;
    setMetadataStatus('submitting');
    try {
      await tenantApi.updateAssetMetadata(tenantId, selectedAssetId, {
        owner: ownerName,
        hosting_provider: hostingProvider,
        tags: assetTags ? assetTags.split(',').map((tag) => tag.trim()).filter(Boolean) : [],
      });
      setMetadataStatus('success');
    } catch (error) {
      console.error('Failed to update asset metadata', error);
      setMetadataStatus('error');
    }
  };

  const handleExportAssets = () => {
    const headers = ['Asset', 'Environment', 'Owner', 'Provider', 'Tags'];
    const rows = filteredAssets.map((asset) => [
      asset.domain || asset.subdomain || asset.asset || '',
      asset.environment || asset.env || '',
      asset.owner || asset.owner_name || '',
      asset.hosting_provider || asset.provider || '',
      Array.isArray(asset.tags) ? asset.tags.join(';') : asset.tags || '',
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tenant-${tenantId}-assets.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleSubscribeMonitoring = async () => {
    setMonitorStatus('submitting');
    try {
      await tenantApi.subscribeAssetMonitoring(tenantId, { scope: 'assets' });
      setMonitorStatus('success');
    } catch (error) {
      console.error('Unable to subscribe to monitoring', error);
      setMonitorStatus('error');
    }
  };

  return (
    <div className="msp-page">
      <TenantHeader
        tenantId={tenantId}
        tenantName="Tenant Assets"
        subtitle="Domains, services, and TLS posture"
      />

      {status === 'loading' && <div className="msp-empty">Loading asset inventory…</div>}

      {status === 'error' && <div className="msp-empty">Unable to load assets.</div>}

      {status === 'ready' && (
        <>
          <div className="msp-toolbar">
            <div className="msp-toolbar-group">
              <input
                type="text"
                className="msp-input"
                placeholder="Filter by tag"
                value={filterTag}
                onChange={(event) => setFilterTag(event.target.value)}
              />
              <select
                className="msp-input"
                value={filterEnvironment}
                onChange={(event) => setFilterEnvironment(event.target.value)}
              >
                <option value="">All environments</option>
                <option value="production">Production</option>
                <option value="staging">Staging</option>
                <option value="development">Development</option>
              </select>
            </div>
            <div className="msp-toolbar-group">
              <button type="button" className="btn btn-secondary" onClick={handleExportAssets}>
                Export Assets
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleSubscribeMonitoring}
                disabled={monitorStatus === 'submitting'}
              >
                {monitorStatus === 'submitting' ? 'Subscribing…' : 'Subscribe to Monitoring'}
              </button>
            </div>
          </div>

          <div className="msp-grid">
            <div className="msp-card">
              <span className="msp-card-label">Primary Domain</span>
              <span className="msp-card-value">{primaryDomain}</span>
              <span className="msp-card-label">
                {verified ? 'Verified' : 'Verification Pending'}
              </span>
            </div>
            <div className="msp-card">
              <span className="msp-card-label">Discovered Subdomains</span>
              <span className="msp-card-value">{subdomains.length}</span>
            </div>
            <div className="msp-card">
              <span className="msp-card-label">Exposed Services</span>
              <span className="msp-card-value">{services.length}</span>
            </div>
            <div className="msp-card">
              <span className="msp-card-label">TLS Certificates</span>
              <span className="msp-card-value">{tlsAssets.length}</span>
            </div>
          </div>

          <div className="msp-section">
            <div className="msp-section-title">Subdomains</div>
            {subdomains.length === 0 ? (
              <div className="msp-empty">No subdomains discovered yet.</div>
            ) : (
              <table className="msp-table">
                <thead>
                  <tr>
                    <th>Subdomain</th>
                    <th>Status</th>
                    <th>Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {subdomains.map((asset) => (
                    <tr key={asset.id || asset.asset_id}>
                      <td>{asset.subdomain || asset.domain}</td>
                      <td>{asset.status || 'Monitored'}</td>
                      <td>{asset.last_seen || asset.lastSeen || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="msp-section">
            <div className="msp-section-title">Exposed Services</div>
            {services.length === 0 ? (
              <div className="msp-empty">No exposed services detected.</div>
            ) : (
              <table className="msp-table">
                <thead>
                  <tr>
                    <th>Asset</th>
                    <th>Service</th>
                    <th>Port</th>
                    <th>Status</th>
                    <th>Exposure Score</th>
                    <th>Evidence</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((asset) => {
                    const evidenceLink =
                      asset.evidence_link ||
                      asset.port_evidence_url ||
                      asset.evidence?.url ||
                      asset.scan_artifact_url;
                    const exposureScore = asset.exposure_score || asset.exposureScore || asset.findings_count || 0;
                    return (
                      <tr key={`${asset.id || asset.asset_id}-${asset.port || asset.service}`}>
                        <td>{asset.asset || asset.domain || 'Asset'}</td>
                        <td>{asset.service || asset.protocol || 'Service'}</td>
                        <td>{asset.port || '—'}</td>
                        <td>{asset.status || 'Open'}</td>
                        <td>{exposureScore}</td>
                        <td>
                          {evidenceLink ? (
                            <a className="msp-link" href={evidenceLink}>
                              View Artifact
                            </a>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="msp-section">
            <div className="msp-section-title">TLS Status</div>
            {tlsAssets.length === 0 ? (
              <div className="msp-empty">No TLS certificate data available.</div>
            ) : (
              <table className="msp-table">
                <thead>
                  <tr>
                    <th>Asset</th>
                    <th>Issuer</th>
                    <th>Expires</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tlsAssets.map((asset) => (
                    <tr key={`${asset.id || asset.asset_id}-tls`}>
                      <td>{asset.domain || asset.asset}</td>
                      <td>{asset.certificate?.issuer || asset.issuer || '—'}</td>
                      <td>{asset.certificate?.expires_at || asset.expires_at || '—'}</td>
                      <td>{asset.certificate?.status || asset.tls_status || 'Valid'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="msp-section">
            <div className="msp-section-title">Asset Ownership & Tags</div>
            {taggedAssets.length === 0 ? (
              <div className="msp-empty">No ownership or tags recorded.</div>
            ) : (
              <table className="msp-table">
                <thead>
                  <tr>
                    <th>Asset</th>
                    <th>Owner</th>
                    <th>Hosting Provider</th>
                    <th>Tags</th>
                  </tr>
                </thead>
                <tbody>
                  {taggedAssets.map((asset) => (
                    <tr key={`${asset.id || asset.asset_id}-tags`}>
                      <td>{asset.domain || asset.asset || asset.subdomain || 'Asset'}</td>
                      <td>{asset.owner || asset.owner_name || '—'}</td>
                      <td>{asset.hosting_provider || asset.provider || '—'}</td>
                      <td>
                        {Array.isArray(asset.tags)
                          ? asset.tags.join(', ')
                          : asset.tags || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="msp-section">
            <div className="msp-section-title">Ownership Workflow</div>
            <form className="msp-form" onSubmit={handleMetadataSave}>
              <label className="msp-form-field">
                Asset
                <select
                  className="msp-input"
                  value={selectedAssetId}
                  onChange={(event) => setSelectedAssetId(event.target.value)}
                >
                  <option value="">Select an asset</option>
                  {assets.map((asset) => {
                    const assetId = asset.id || asset.asset_id;
                    const assetName = asset.domain || asset.subdomain || asset.asset || assetId;
                    return (
                      <option key={assetId} value={assetId}>
                        {assetName}
                      </option>
                    );
                  })}
                </select>
              </label>
              <label className="msp-form-field">
                Owner/Contact
                <input
                  type="text"
                  className="msp-input"
                  value={ownerName}
                  onChange={(event) => setOwnerName(event.target.value)}
                />
              </label>
              <label className="msp-form-field">
                Hosting Provider
                <input
                  type="text"
                  className="msp-input"
                  value={hostingProvider}
                  onChange={(event) => setHostingProvider(event.target.value)}
                />
              </label>
              <label className="msp-form-field">
                Tags
                <input
                  type="text"
                  className="msp-input"
                  placeholder="critical service, hosting provider"
                  value={assetTags}
                  onChange={(event) => setAssetTags(event.target.value)}
                />
              </label>
              <button type="submit" className="btn btn-primary" disabled={metadataStatus === 'submitting'}>
                {metadataStatus === 'submitting' ? 'Saving…' : 'Save Metadata'}
              </button>
            </form>
            {metadataStatus === 'error' && <div className="msp-empty">Unable to save metadata.</div>}
          </div>
        </>
      )}
    </div>
  );
}

export default TenantAssets;
